
import { Link } from 'react-router-dom';

export default function HealthCheckupExplanation() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900">健康診断結果解説</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-teal-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/03dc4f38411110415bbc6204e14cdd13.jpg"
            alt="健康診断結果解説"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            健康診断結果<span className="text-teal-600">解説</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            血液検査、尿検査などの結果を薬剤師がわかりやすく説明し、改善点をアドバイスします
          </p>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">健康診断結果を正しく理解しましょう</h2>
              <p className="text-lg text-gray-600 mb-8">
                健康診断の結果表を受け取っても、数値の意味や改善方法がわからないことはありませんか？
                薬剤師が専門知識を活かして、あなたの健康状態を詳しく解説し、具体的な改善策をご提案します。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">わかりやすい説明</h3>
                    <p className="text-gray-600">専門用語を使わず、誰でも理解できるように丁寧に説明します</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">個別対応</h3>
                    <p className="text-gray-600">あなたの生活習慣や体質に合わせた改善策をご提案</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">継続サポート</h3>
                    <p className="text-gray-600">改善後のフォローアップも含めた長期的なサポート</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Healthcare%20professional%20reviewing%20medical%20test%20results%20with%20patient%2C%20detailed%20health%20checkup%20consultation%2C%20medical%20charts%20and%20laboratory%20reports%20on%20desk%2C%20professional%20medical%20consultation%20scene%2C%20modern%20healthcare%20setting&width=500&height=600&seq=health-checkup-consultation&orientation=portrait"
                alt="健康診断相談"
                className="w-full max-w-md h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Test Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">解説対象の検査項目</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-drop-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">血液検査</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 血糖値・HbA1c</li>
                <li>• コレステロール値</li>
                <li>• 中性脂肪</li>
                <li>• 肝機能（AST、ALT）</li>
                <li>• 腎機能（クレアチニン）</li>
                <li>• 貧血検査（ヘモグロビン）</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-yellow-50 text-yellow-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-test-tube-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">尿検査</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 尿糖</li>
                <li>• 尿蛋白</li>
                <li>• 尿潜血</li>
                <li>• 尿比重</li>
                <li>• 尿沈渣</li>
                <li>• 尿酸値</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">循環器検査</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 血圧測定</li>
                <li>• 心電図</li>
                <li>• 胸部X線</li>
                <li>• 心拍数</li>
                <li>• 動脈硬化度</li>
                <li>• 血管年齢</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-scales-3-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">身体測定</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• BMI</li>
                <li>• 体脂肪率</li>
                <li>• 腹囲</li>
                <li>• 筋肉量</li>
                <li>• 基礎代謝</li>
                <li>• 内臓脂肪レベル</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">その他の検査</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 視力・聴力検査</li>
                <li>• 眼底検査</li>
                <li>• 眼圧検査</li>
                <li>• 肺機能検査</li>
                <li>• 骨密度検査</li>
                <li>• 腫瘍マーカー</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-microscope-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">特殊検査</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 甲状腺機能</li>
                <li>• 炎症反応（CRP）</li>
                <li>• アレルギー検査</li>
                <li>• ピロリ菌検査</li>
                <li>• 便潜血検査</li>
                <li>• PSA（前立腺）</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Service Flow */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">保険薬局での健康サポート機能</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">結果表持参</h3>
              <p className="text-gray-600">健康診断の結果表をお持ちください</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">詳細解説</h3>
              <p className="text-gray-600">各項目の意味と現在の状態を説明</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">改善提案</h3>
              <p className="text-gray-600">生活習慣の改善点を具体的にアドバイス</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">継続サポート</h3>
              <p className="text-gray-600">定期的なフォローアップで改善をサポート</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">よくある質問</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. 健康診断の結果表だけで相談できますか？</h3>
              <p className="text-gray-600">A. はい、健康診断の結果表があれば詳しく解説いたします。過去の結果表もお持ちいただければ、経年変化も含めて説明できます。</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. 薬を飲んでいても相談できますか？</h3>
              <p className="text-gray-600">A. もちろんです。現在服用中のお薬との関係も含めて、総合的にアドバイスいたします。お薬手帳もお持ちください。</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. 相談にはどのくらい時間がかかりますか？</h3>
              <p className="text-gray-600">A. 通常30分程度ですが、検査項目の数や質問内容によって前後します。十分な時間を確保してご相談いただけます。</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. 改善方法は具体的に教えてもらえますか？</h3>
              <p className="text-gray-600">A. はい、食事や運動、生活習慣の具体的な改善方法をお伝えします。必要に応じて資料もお渡しいたします。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">健康診断結果について相談しませんか？</h2>
          <p className="text-xl text-teal-100 mb-8">
            専門薬剤師があなたの健康状態を詳しく解説し、改善のお手伝いをいたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/health-support"
              className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              健康サポートに戻る
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              ホームに戻る
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-consultation/health-checkup-explanation" className="hover:text-white cursor-pointer">健康診断結果解説</Link></li>
                <li><Link to="/health-consultation/lifestyle-disease-prevention" className="hover:text-white cursor-pointer">生活習慣病予防</Link></li>
                <li><Link to="/health-consultation/medication-therapy-consultation" className="hover:text-white cursor-pointer">薬物療法相談</Link></li>
                <li><Link to="/health-consultation/health-management-plan" className="hover:text-white cursor-pointer">健康管理計画</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
